/* TIGERSECU Series A. Standalone Shopify section behavior; reservations continue on the configured TIGERSECU page. */
(() => {
  'use strict';

  const RUNTIME_KEY = '__tigersecuSeriesAStandalone';
  const ROOT_SELECTOR = '.tsap-root[data-tsap-root]';
  if (window[RUNTIME_KEY]) {
    window[RUNTIME_KEY].init(document);
    return;
  }

  const instances = new Map();
  const lockedDialogs = new Set();
  let previousBodyOverflow = null;

  function lockBody(dialog) {
    if (lockedDialogs.has(dialog)) return;
    if (!lockedDialogs.size) previousBodyOverflow = document.body.style.overflow;
    lockedDialogs.add(dialog);
    document.body.style.overflow = 'hidden';
  }

  function unlockBody(dialog) {
    if (!lockedDialogs.delete(dialog) || lockedDialogs.size) return;
    document.body.style.overflow = previousBodyOverflow === null ? '' : previousBodyOverflow;
    previousBodyOverflow = null;
  }

  function createInstance(root) {
    const configNode = root.querySelector('script[type="application/json"][data-tsap-config]');
    if (!configNode) return null;
    let config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      console.error('TIGERSECU: invalid section configuration.', error);
      return null;
    }

    const abortController = new AbortController();
    const signal = abortController.signal;
    const $ = (selector) => root.querySelector(selector);
    const $$ = (selector) => [...root.querySelectorAll(selector)];
    const byId = (logicalId) => root.querySelector(`[data-tsap-id="${logicalId}"]`);
    const listen = (target, type, handler, options = {}) => {
      if (target) target.addEventListener(type, handler, { ...options, signal });
    };
    const features = config.features || {};
    const kits = Array.isArray(config.kits) ? config.kits : [];
    const tabs = $$('.v13-feature-tab, .tsap-feature-tab').filter((tab) => features[tab.dataset.feature]);
    const featureImage = byId('feature-image');
    const featureEyebrow = byId('feature-eyebrow');
    const featureTitle = byId('feature-title');
    const featureDescription = byId('feature-description');
    const featureAction = byId('feature-action');
    const featureNote = byId('feature-note');
    const featurePanel = byId('feature-panel');
    const mediaDialog = byId('media-dialog');
    const video = byId('demo-video');
    const expandedImage = byId('expanded-image');
    const mediaCaption = byId('media-caption');
    const dialogFocus = new Map();
    const openDialogs = new Set();
    let feature = tabs.some((tab) => tab.dataset.feature === 'text') ? 'text' : tabs[0]?.dataset.feature;
    let capacity = Number($('[data-capacity][aria-pressed="true"]')?.dataset.capacity) || 2;
    let guideFrame = null;
    let resizeObserver = null;
    let accessoryDrag = null;
    let destroyed = false;

    function setText(logicalId, value) {
      const node = byId(logicalId);
      if (node) node.textContent = value;
    }

    function chooseFeature(key, focus = false) {
      const data = features[key];
      const activeTab = tabs.find((tab) => tab.dataset.feature === key);
      if (!data || !activeTab) return;
      feature = key;
      tabs.forEach((tab) => {
        const active = tab === activeTab;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      if (featureImage && data.image) {
        featureImage.removeAttribute('srcset');
        featureImage.removeAttribute('sizes');
        featureImage.src = data.image;
        featureImage.alt = data.alt || '';
      }
      if (featureEyebrow) featureEyebrow.textContent = data.eyebrow || '';
      if (featureTitle) featureTitle.textContent = data.title || '';
      if (featureDescription) featureDescription.textContent = data.description || '';
      if (featureNote) featureNote.textContent = data.note || '';
      if (featureAction) {
        featureAction.textContent = `${data.video ? '▶' : '⤢'} ${data.button || (data.video ? 'Watch the demo' : 'Enlarge feature view')}`;
      }
      if (featurePanel && activeTab.id) featurePanel.setAttribute('aria-labelledby', activeTab.id);
      if (focus) {
        activeTab.focus({ preventScroll: true });
        activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
      }
    }

    tabs.forEach((tab, index) => {
      listen(tab, 'click', () => chooseFeature(tab.dataset.feature));
      listen(tab, 'keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          chooseFeature(tabs[next].dataset.feature, true);
        }
      });
    });

    function stopVideo() {
      if (!video) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
    }

    function openDialog(dialog) {
      if (!dialog || dialog.open) return;
      dialogFocus.set(dialog, document.activeElement);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else {
        dialog.setAttribute('open', '');
        dialog.setAttribute('aria-modal', 'true');
        dialog.querySelector('[data-close],button,[tabindex="0"]')?.focus();
      }
      openDialogs.add(dialog);
      lockBody(dialog);
    }

    function finishDialogClose(dialog, restoreFocus = true) {
      if (dialog === mediaDialog) stopVideo();
      openDialogs.delete(dialog);
      unlockBody(dialog);
      const lastFocus = dialogFocus.get(dialog);
      dialogFocus.delete(dialog);
      if (restoreFocus && lastFocus?.isConnected && !openDialogs.size) {
        lastFocus.focus({ preventScroll: true });
      }
    }

    function closeDialog(dialog, restoreFocus = true) {
      if (!dialog) return;
      if (dialog.open) {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
      }
      finishDialogClose(dialog, restoreFocus);
    }

    $$('dialog').forEach((dialog) => {
      listen(dialog, 'cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });
      listen(dialog, 'close', () => {
        if (openDialogs.has(dialog)) finishDialogClose(dialog);
      });
      listen(dialog, 'click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(dialog);
      });
      dialog.querySelectorAll('[data-close]').forEach((button) => {
        listen(button, 'click', () => closeDialog(dialog));
      });
      listen(dialog, 'keydown', (event) => {
        if (event.key === 'Escape' && typeof dialog.showModal !== 'function') {
          event.preventDefault();
          closeDialog(dialog);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = [...dialog.querySelectorAll(
          'button:not([disabled]),a[href],input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),video[controls],[tabindex]:not([tabindex="-1"])'
        )].filter((node) => !node.hidden && node.getClientRects().length);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault(); last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first.focus();
        }
      });
    });

    function enlargeFeature() {
      const data = features[feature];
      if (!data || !expandedImage || !data.image) return;
      stopVideo();
      expandedImage.src = data.image;
      expandedImage.alt = data.alt || '';
      expandedImage.hidden = false;
      if (video) video.hidden = true;
      if (mediaCaption) mediaCaption.textContent = `Series A · ${tabs.find((tab) => tab.dataset.feature === feature)?.textContent.trim() || ''} · Illustrative interface`;
      openDialog(mediaDialog);
    }

    function openFeatureDemo(key) {
      const data = features[key];
      if (!data || !video || !data.video) {
        enlargeFeature();
        return;
      }
      if (expandedImage) expandedImage.hidden = true;
      video.hidden = false;
      video.src = data.video;
      video.dataset.demo = key;
      video.load();
      if (mediaCaption) mediaCaption.textContent = `Series A · ${data.demoTitle || ''} · Feature demonstration`;
      openDialog(mediaDialog);
      const play = video.play();
      if (play?.catch) play.catch(() => {});
    }

    listen(byId('zoom-feature'), 'click', enlargeFeature);
    listen(featureAction, 'click', () => {
      if (features[feature]?.video) openFeatureDemo(feature);
      else enlargeFeature();
    });
    $$('[data-demo]').forEach((button) => {
      listen(button, 'click', () => {
        chooseFeature(button.dataset.demo);
        openFeatureDemo(button.dataset.demo);
      });
    });
    listen(video, 'error', () => {
      if (mediaDialog?.open && !video.hidden && mediaCaption) {
        mediaCaption.textContent = 'The video could not be loaded. Please close this viewer and try again.';
      }
    });

    // available describes a configured reservation destination, never inventory.
    function reservationDestination(kit) {
      if (!kit || kit.available !== true || typeof kit.reservationUrl !== 'string') return null;
      const destination = kit.reservationUrl.trim();
      if (!/^https?:\/\//i.test(destination)) return null;
      try {
        const parsed = new URL(destination);
        return ['http:', 'https:'].includes(parsed.protocol) && parsed.hostname ? parsed.href : null;
      } catch (_) {
        return null;
      }
    }

    function canReserve(kit) {
      if (!reservationDestination(kit) || !Array.isArray(kit.prices)) return false;
      const total = Number(kit.prices[0]);
      const retail = Number(kit.retail);
      const deposit = Number(kit.deposit);
      return Number.isSafeInteger(total) && Number.isSafeInteger(retail) && Number.isSafeInteger(deposit) &&
        deposit > 0 && total >= deposit && retail >= total;
    }

    $$('[data-reserve]').forEach((button) => {
      listen(button, 'click', (event) => {
        const kit = kits.find((item) => item.id === button.dataset.kit);
        if (button.tagName !== 'A' || button.disabled || button.getAttribute('aria-disabled') === 'true' || !canReserve(kit)) {
          event.preventDefault();
          return;
        }
        // Follow this kit's configured link normally, with no extra parameters.
        button.href = reservationDestination(kit);
      });
    });

    const termsToggle = byId('v14-terms-toggle');
    const termsPanel = byId('v14-terms-panel');
    if (termsToggle && termsPanel) {
      listen(termsToggle, 'click', () => {
        const expanded = termsToggle.getAttribute('aria-expanded') !== 'true';
        termsToggle.setAttribute('aria-expanded', String(expanded));
        termsPanel.hidden = !expanded;
      });
    }

    const channelSelect = byId('channel-count');
    function updateEstimate() {
      if (!channelSelect) return;
      const channels = Number(channelSelect.value);
      const baseline = { 1: 32, 4: 8, 8: 4 }[channels];
      if (!baseline || ![2, 4].includes(capacity)) return;
      const days = baseline * capacity / 2;
      setText('recording-days', String(days));
      setText('recording-summary', `${capacity}TB · ${channels} ${channels === 1 ? 'channel' : 'channels'} · continuous 24/7 recording`);
      $$('.tsap-mini-bars i').forEach((bar, index) => {
        bar.classList.toggle('tsap-on', index < Math.min(8, Math.ceil(days / 2)));
      });
    }
    $$('[data-capacity]').forEach((button) => {
      listen(button, 'click', () => {
        const next = Number(button.dataset.capacity);
        if (![2, 4].includes(next)) return;
        capacity = next;
        $$('[data-capacity]').forEach((item) => item.setAttribute('aria-pressed', String(Number(item.dataset.capacity) === capacity)));
        updateEstimate();
      });
    });
    listen(channelSelect, 'change', updateEstimate);

    const accessoryTrack = byId('accessory-track');
    const accessoryCards = $$('.tsap-accessory-card');
    const accessoryPrev = byId('accessory-prev');
    const accessoryNext = byId('accessory-next');
    const accessoryPosition = byId('accessory-position');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function accessoryMetrics() {
      if (!accessoryTrack || !accessoryCards.length) return { step: 1, visible: 0, max: 0 };
      const gap = parseFloat(getComputedStyle(accessoryTrack).gap) || 0;
      const step = accessoryCards[0].getBoundingClientRect().width + gap;
      if (step <= 0) return { step: 1, visible: 0, max: 0 };
      return {
        step,
        visible: Math.max(1, Math.floor((accessoryTrack.clientWidth + gap + 1) / step)),
        max: Math.max(0, accessoryTrack.scrollWidth - accessoryTrack.clientWidth)
      };
    }

    function updateAccessories() {
      if (!accessoryTrack) return;
      const { step, visible, max } = accessoryMetrics();
      const first = Math.min(Math.max(0, accessoryCards.length - 1), Math.max(0, Math.round(accessoryTrack.scrollLeft / step)));
      if (accessoryPosition) accessoryPosition.textContent = accessoryCards.length ? `${first + 1}–${Math.min(accessoryCards.length, first + visible)} / ${accessoryCards.length}` : '0 / 0';
      if (accessoryPrev) accessoryPrev.disabled = accessoryTrack.scrollLeft <= 2;
      if (accessoryNext) accessoryNext.disabled = accessoryTrack.scrollLeft >= max - 2;
    }

    function scrollAccessories(left) {
      if (!accessoryTrack) return;
      accessoryTrack.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    function moveAccessories(direction) {
      const { step, visible, max } = accessoryMetrics();
      if (!visible || !accessoryTrack) return;
      const target = Math.round(accessoryTrack.scrollLeft / step) * step + direction * visible * step;
      scrollAccessories(Math.max(0, Math.min(max, target)));
    }
    listen(accessoryPrev, 'click', () => moveAccessories(-1));
    listen(accessoryNext, 'click', () => moveAccessories(1));
    listen(accessoryTrack, 'scroll', updateAccessories, { passive: true });
    listen(accessoryTrack, 'keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        moveAccessories(event.key === 'ArrowRight' ? 1 : -1);
      }
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        scrollAccessories(event.key === 'Home' ? 0 : accessoryMetrics().max);
      }
    });
    listen(accessoryTrack, 'pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      accessoryDrag = { id: event.pointerId, start: event.clientX, left: accessoryTrack.scrollLeft };
      accessoryTrack.classList.add('tsap-is-dragging');
      accessoryTrack.setPointerCapture(event.pointerId);
      event.preventDefault();
      accessoryTrack.focus({ preventScroll: true });
    });
    listen(accessoryTrack, 'pointermove', (event) => {
      if (accessoryDrag && event.pointerId === accessoryDrag.id) accessoryTrack.scrollLeft = accessoryDrag.left + accessoryDrag.start - event.clientX;
    });
    function endAccessoryDrag(event) {
      if (!accessoryDrag || event.pointerId !== accessoryDrag.id) return;
      accessoryDrag = null;
      accessoryTrack.classList.remove('tsap-is-dragging');
      if (accessoryTrack.hasPointerCapture(event.pointerId)) accessoryTrack.releasePointerCapture(event.pointerId);
      updateAccessories();
    }
    listen(accessoryTrack, 'pointerup', endAccessoryDrag);
    listen(accessoryTrack, 'pointercancel', endAccessoryDrag);
    listen(accessoryTrack, 'lostpointercapture', endAccessoryDrag);

    const pageTargets = [
      { id: 'overview', name: 'Overview' },
      { id: 'ai-search', name: 'Smart Features' },
      { id: 'kits', name: 'Kits & Pricing' },
      { id: 'vip-offer', name: 'Offers' },
      { id: 'faq', name: 'FAQ' }
    ];
    const landingHeader = $('.tsap-landing-header');
    const landingToggle = $('.tsap-landing-toggle');
    const landingLinks = byId('landing-links');
    const landingCurrent = $('.tsap-landing-current');
    const pageLinks = landingHeader ? [...landingHeader.querySelectorAll('[data-page-anchor]')] : [];
    let currentChapter = 'overview';
    let measuredHeaderHeight = null;

    function updateToggleLabel() {
      if (!landingToggle) return;
      const currentName = pageTargets.find((target) => target.id === currentChapter)?.name || 'Overview';
      const action = landingToggle.getAttribute('aria-expanded') === 'true' ? 'Close' : 'Open';
      landingToggle.setAttribute('aria-label', `${action} page navigation. Current section: ${currentName}`);
    }

    function closeNavigation(restoreFocus = false) {
      if (!landingHeader || !landingToggle) return;
      const focusInside = landingLinks?.contains(document.activeElement);
      landingHeader.classList.remove('tsap-is-open');
      landingToggle.setAttribute('aria-expanded', 'false');
      updateToggleLabel();
      measureHeader();
      if (!destroyed) queueGuide();
      if (restoreFocus && focusInside && landingToggle.getClientRects().length) {
        landingToggle.focus({ preventScroll: true });
      }
    }

    function markCurrent(id) {
      currentChapter = id;
      pageLinks.forEach((link) => {
        const active = link.dataset.pageAnchor === id;
        link.classList.toggle('tsap-is-current', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      if (landingCurrent) landingCurrent.textContent = pageTargets.find((target) => target.id === id)?.name || 'Overview';
      updateToggleLabel();
    }

    function measureHeader() {
      if (!landingHeader) return 0;
      const height = Math.ceil(landingHeader.getBoundingClientRect().height);
      if (height > 0 && height !== measuredHeaderHeight) {
        measuredHeaderHeight = height;
        root.style.setProperty('--tsap-measured-header-height', `${height}px`);
      }
      return height;
    }

    function updateGuide() {
      guideFrame = null;
      if (destroyed) return;
      const threshold = measureHeader() + 24;
      let current = 'overview';
      pageTargets.forEach((target) => {
        const section = byId(target.id);
        if (section && section.getBoundingClientRect().top <= threshold) current = target.id;
      });
      markCurrent(current);
    }

    function queueGuide() {
      if (guideFrame === null) guideFrame = requestAnimationFrame(updateGuide);
    }

    listen(landingToggle, 'click', () => {
      if (!landingHeader || !landingLinks) return;
      const open = landingToggle.getAttribute('aria-expanded') !== 'true';
      landingHeader.classList.toggle('tsap-is-open', open);
      landingToggle.setAttribute('aria-expanded', String(open));
      updateToggleLabel();
    });
    pageLinks.forEach((link) => {
      listen(link, 'click', () => {
        closeNavigation();
        markCurrent(link.dataset.pageAnchor);
      });
    });
    if (landingHeader) {
      landingHeader.querySelectorAll('.tsap-landing-reserve').forEach((link) => {
        listen(link, 'click', () => closeNavigation());
      });
      landingHeader.classList.add('tsap-has-js');
    }
    listen(document, 'click', (event) => {
      if (landingHeader && !landingHeader.contains(event.target)) closeNavigation();
    });
    listen(document, 'keydown', (event) => {
      if (event.key === 'Escape') closeNavigation(true);
    });
    listen(window, 'scroll', queueGuide, { passive: true });
    listen(window, 'resize', () => {
      if (window.innerWidth > 1000) closeNavigation();
      measureHeader();
      updateAccessories();
      queueGuide();
    });
    listen(window, 'load', () => { updateAccessories(); queueGuide(); }, { once: true });
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => { updateAccessories(); queueGuide(); });
      resizeObserver.observe(root);
      if (landingHeader) resizeObserver.observe(landingHeader);
      if (accessoryTrack) resizeObserver.observe(accessoryTrack);
    }

    chooseFeature(feature);
    updateEstimate();
    updateAccessories();
    updateGuide();
    root.dataset.tsapReady = 'true';

    return {
      refresh() { updateAccessories(); queueGuide(); },
      selectBlock(target) {
        const details = target?.closest?.('details');
        if (details && root.contains(details)) details.open = true;
      },
      destroy() {
        if (destroyed) return;
        destroyed = true;
        abortController.abort();
        resizeObserver?.disconnect();
        if (guideFrame !== null) cancelAnimationFrame(guideFrame);
        if (accessoryDrag && accessoryTrack?.hasPointerCapture(accessoryDrag.id)) accessoryTrack.releasePointerCapture(accessoryDrag.id);
        accessoryDrag = null;
        accessoryTrack?.classList.remove('tsap-is-dragging');
        [...openDialogs].forEach((dialog) => closeDialog(dialog, false));
        stopVideo();
        closeNavigation();
        landingHeader?.classList.remove('tsap-has-js');
        root.style.removeProperty('--tsap-measured-header-height');
        delete root.dataset.tsapReady;
      }
    };
  }

  function init(scope) {
    // Some theme-editor replacements dispatch load after detaching the old root.
    [...instances].forEach(([root, instance]) => {
      if (!root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    });
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = createInstance(root);
      if (instance) instances.set(root, instance);
    });
  }

  function affectedInstances(scope) {
    return [...instances].filter(([root]) => root === scope || scope?.contains?.(root));
  }
  window[RUNTIME_KEY] = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    affectedInstances(event.target).forEach(([root, instance]) => {
      instance.destroy();
      instances.delete(root);
    });
  });
  document.addEventListener('shopify:section:select', (event) => {
    affectedInstances(event.target).forEach(([, instance]) => instance.refresh());
  });
  document.addEventListener('shopify:block:select', (event) => {
    [...instances].filter(([root]) => root.contains(event.target)).forEach(([, instance]) => instance.selectBlock(event.target));
  });
})();
